import apiClient from '../client';
import { API_ENDPOINTS } from '../endpoints';
import type {
  RegisterRequest,
  LoginRequest,
  ChangePasswordRequest,
  RegisterResponse,
  LoginResponse,
  CurrentUserResponse,
  ChangePasswordResponse,
} from '../types/auth.types';

export const authService = {
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    // Create FormData for file uploads
    const formData = new FormData();
    
    // Add basic fields
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('password', data.password);
    formData.append('password_confirmation', data.password_confirmation);
    formData.append('type', data.type || 'client');
    
    if (data.phone) {
      formData.append('phone', data.phone);
    }
    
    // Add optional profile fields
    if (data.company_name) {
      formData.append('company_name', data.company_name);
    }
    if (data.address) {
      formData.append('address', data.address);
    }
    if (data.description) {
      formData.append('description', data.description);
    }
    if (data.license_number) {
      formData.append('license_number', data.license_number);
    }
    if (data.tax_id) {
      formData.append('tax_id', data.tax_id);
    }
    
    // Add profile image if provided
    if (data.profile_image) {
      formData.append('profile_image', data.profile_image);
    }
    
    // Handle specializations array
    if (data.specialization_ids && Array.isArray(data.specialization_ids)) {
      data.specialization_ids.forEach((id: number, index: number) => {
        formData.append(`specialization_ids[${index}]`, id.toString());
      });
    }
    
    // Add required documents for client registration
    if (data.commercial_register) {
      formData.append('commercial_register', data.commercial_register);
    }
    if (data.tax_certificate) {
      formData.append('tax_certificate', data.tax_certificate);
    }
    if (data.iban_certificate) {
      formData.append('iban_certificate', data.iban_certificate);
    }
    if (data.zakat_income_certificate) {
      formData.append('zakat_income_certificate', data.zakat_income_certificate);
    }
    if (data.social_insurance_certificate) {
      formData.append('social_insurance_certificate', data.social_insurance_certificate);
    }
    if (data.company_profile) {
      formData.append('company_profile', data.company_profile);
    }
    
    const response = await apiClient.post<RegisterResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      data
    );
    return response.data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
  },

  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    const response = await apiClient.get<CurrentUserResponse>(
      API_ENDPOINTS.AUTH.ME
    );
    return response.data;
  },

  changePassword: async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    const response = await apiClient.post<ChangePasswordResponse>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      data
    );
    return response.data;
  },
};
